import { Link } from "react-router-dom";
import { ShieldCheck, Pill, Stethoscope, Infinity, FlaskConical } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

const Footer = () => {
  return (
    <footer className="border-t border-border bg-card">
      {/* Trust bar */}
      <div className="border-b border-border">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-wrap justify-center gap-8 md:gap-16 text-sm text-muted-foreground">
            <span className="flex items-center gap-2">
              <Stethoscope size={18} className="text-champagne" /> Clinician-Guided
            </span>
            <span className="flex items-center gap-2">
              <ShieldCheck size={18} className="text-champagne" /> HIPAA Compliant
            </span>
            <span className="flex items-center gap-2">
              <Pill size={18} className="text-champagne" /> 503A Pharmacies
            </span>
            <span className="flex items-center gap-2">
              <FlaskConical size={18} className="text-champagne" /> 3rd-Party COA Tested
            </span>
            <span className="flex items-center gap-2">
              <Infinity size={18} className="text-champagne" /> Long-Term Partnership
            </span>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-5 gap-8">
          <div className="space-y-3">
            <BrandLogo />
            <p className="text-sm text-muted-foreground leading-relaxed">
              Care that continues, even when insurance does not.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-foreground text-sm mb-3">Platform</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/peptides" className="hover:text-foreground transition-colors">Therapies</Link></li>
              <li><a href="/#how-it-works" className="hover:text-foreground transition-colors">How It Works</a></li>
              <li><a href="/#pricing" className="hover:text-foreground transition-colors">Pricing</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-foreground text-sm mb-3">Company</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/about" className="hover:text-foreground transition-colors">About</Link></li>
              <li><Link to="/careers" className="hover:text-foreground transition-colors">Careers</Link></li>
              <li><Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-foreground text-sm mb-3">Partners</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/join/provider" className="hover:text-foreground transition-colors">For Providers</Link></li>
              <li><Link to="/join/sales" className="hover:text-foreground transition-colors">For Sales Partners</Link></li>
              <li><Link to="/join/pharmacy" className="hover:text-foreground transition-colors">For Pharmacies</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-foreground text-sm mb-3">Legal</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-foreground transition-colors">Terms of Service</Link></li>
              <li><Link to="/hipaa" className="hover:text-foreground transition-colors">HIPAA Notice</Link></li>
              <li><Link to="/medical-disclaimer" className="hover:text-foreground transition-colors">Medical Disclaimer</Link></li>
            </ul>
          </div>
        </div>

        {/* Mission */}
        <div className="border-t border-border mt-12 pt-8">
          <p className="text-center text-sm text-muted-foreground italic max-w-2xl mx-auto">
            Elara Health delivers clinician-guided metabolic care that supports long-term transformation with continuity, personalization, and ongoing clinical oversight.
          </p>
        </div>

        {/* Platform vs Provider + State Availability — Compliance */}
        <div className="border-t border-border mt-8 pt-8 text-xs text-muted-foreground max-w-3xl mx-auto leading-relaxed text-center space-y-2">
          <p>
            Elara Health is a technology and administrative platform. Clinical care is delivered by independently licensed physicians and affiliated medical practices. <strong>Elara Health does not practice medicine.</strong>
          </p>
          <p>
            Services are only available in states where our providers are licensed. Patient–provider relationships are formed with the treating physician, not with Elara Health.
          </p>
        </div>

        {/* FDA & Compliance Disclaimers */}
        <div className="border-t border-border mt-8 pt-8 space-y-3 text-xs text-muted-foreground max-w-3xl mx-auto">
          <details className="group">
            <summary className="cursor-pointer select-none font-semibold hover:text-foreground transition-colors">
              Important Safety Information
            </summary>
            <p className="mt-2 leading-relaxed">
              GLP-1 receptor agonist therapy may not be appropriate for all patients. During your clinical assessment, your provider will evaluate factors including body composition, medical history, and current health status to determine eligibility. Certain conditions — such as a history of medullary thyroid carcinoma, multiple endocrine neoplasia syndrome type 2, pancreatitis, or active eating disorders — may affect candidacy. Patients who are pregnant, breastfeeding, or planning to conceive should not use GLP-1 medications. Your provider will discuss all options and risks with you during your consultation.
            </p>
          </details>
        </div>
        <div className="space-y-3 text-xs text-muted-foreground max-w-3xl mx-auto mt-4">
          <p>
            <strong>Compounding Disclosure:</strong> Elara Health partners with licensed U.S. compounding pharmacies that are registered with the FDA and adhere to strict quality and regulatory standards. All partner pharmacies are required to perform endotoxin testing and provide third-party Certificates of Analysis (COA) for every compounded product. When clinically appropriate, medications may be compounded to support individualized treatment plans. Compounded medications are prepared for specific patients and, as such, are not reviewed or approved by the FDA in the same manner as commercially manufactured drugs. All treatments are prescribed and overseen by licensed clinicians.
          </p>
          <p>
            <strong>Prescription Requirement:</strong> All medications are prescribed by licensed healthcare providers based on individual medical evaluation. Not all patients will qualify.
          </p>
          <p>
            <strong>Results Disclaimer:</strong> Individual results may vary. Weight loss outcomes depend on multiple factors including adherence, lifestyle, and medical history.
          </p>
          <p>
            <strong>Safety:</strong> Treatment may involve potential risks and side effects. A licensed provider will determine if treatment is appropriate for you.
          </p>
          <p>
            <strong>Provider Independence:</strong> All healthcare providers on the Elara Health platform are independently licensed clinicians who exercise independent clinical judgment. Elara Health does not direct, control, or influence provider medical decisions. Treatment recommendations, prescriptions, and clinical determinations are made solely by your provider based on their professional assessment.
          </p>
        </div>

        <div className="border-t border-border mt-8 pt-8 text-center text-xs text-muted-foreground">
          <p>© {new Date().getFullYear()} Elara Health · Long Island, New York</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
